import { AccountController } from "@/app/ui/controllers/AccountController";
import { CargoController } from "@/app/ui/controllers/CargoController";
import { ReadyToCargoController } from "@/app/ui/controllers/ReadyToCargoController";
import { FlagsPermissionCargo } from "@/feature-flags/flags-permission-cargo";
import { FlagsPermissionCargoV2 } from "@/feature-flags/flags-permission-cargo-v2";

export const cargoRect = (product: string) => {
  const locale = AccountController.accountData.countryCode === "my";
  let rect = 0;
  switch (product) {
    case "ship":
      rect = locale ? 21 : 25;
      break;
    case "truck":
      rect = 25;
      break;
    case "train":
      rect = locale ? 24 : 27;
      break;
    case "plane":
      rect = 29;
      break;
    default:
      break;
  }
  return rect;
};

export const productRect = (product: string) => {
  let rect = 0;
  switch (product) {
    case "REGULAR":
      rect = 18;
      break;
    case "EXPRESS":
      rect = 18;
      break;
    default:
      break;
  }
  return rect;
};

export const cargoTypeData = (type: string) => {
  let styles = {
    label: "",
    icon: ""
  };

  switch (type) {
    case "truck":
      styles = {
        label: "Truk",
        icon: "cargo-truck"
      };
      break;
    case "train":
      styles = {
        label: "Kereta",
        icon: "cargo-train"
      };
      break;
    case "plane":
      styles = {
        label: "Pesawat",
        icon: "cargo-plane"
      };
      break;
    case "ship":
      styles = {
        label: "Kapal",
        icon: "ship"
      };
      break;

    default:
      break;
  }
  return styles;
};

export const layoutTransitPlaneLabel = (transitLength: number) => {
  const topLineHeight: any = {
    0: 78,
    1: 78,
    2: 76,
    3: 73,
    4: 70
  };

  const topTransitHeight: any = {
    0: 82,
    1: 82,
    2: 79,
    3: 76,
    4: 73
  };

  const fontTransitList: any = {
    0: 12,
    1: 12,
    2: 10,
    3: 10,
    4: 8
  };

  const topCargoNumber: any = {
    0: 75,
    1: 75,
    2: 73,
    3: 72,
    4: 68.5
  };

  const fontCargoNumber: any = {
    0: 32,
    1: 32,
    2: 32,
    3: 32,
    4: 20
  };

  return {
    topLineHeight: topLineHeight[transitLength],
    fontTransitList: fontTransitList[transitLength],
    topTransitHeight: topTransitHeight[transitLength],
    topCargoNumber: topCargoNumber[transitLength],
    fontCargoNumber: fontCargoNumber[transitLength]
  };
};

const checkIsUseSabre = (cargoType: any, cargoTypeStatus: any) => {
  return (
    FlagsPermissionCargo.feature_flight_v2_enable.isEnabled() &&
    cargoType === "plane" &&
    cargoTypeStatus !== "manual"
  );
};

const checkIsRtcPlaneAuto = (
  cargoType: any,
  cargoTypeStatus: any,
  flowBooking: any
) => {
  return (
    cargoType === "plane" &&
    cargoTypeStatus === "auto" &&
    flowBooking.includes("rtc")
  );
};

const getReturnPath = (conditionRedirect: boolean) => {
  let returnPath = "/cargo";
  if (conditionRedirect) {
    returnPath = "/cargo/booking-canceled";
  }
  return returnPath;
};

const resetCargoData = (
  conditionRedirect: boolean,
  router: any,
  returnPath: string
) => {
  CargoController.resetFormDataFlight();
  CargoController._resetCargoDataAfterFailedBookingCargo();

  if (!CargoController.readyToCargoList.data.length || conditionRedirect) {
    CargoController.setIsFromCutOff(true);
    router.push(returnPath);
  }
};

export const handleFailedBookingNavigation = (params: {
  router: any;
  rcId: any;
  cargoType: any;
  cargoTypeStatus: any;
  flowBooking: any;
}) => {
  const isRebooking = !!params.rcId;
  const isUseSabre = checkIsUseSabre(params.cargoType, params.cargoTypeStatus);

  const isRtcPlaneAuto = checkIsRtcPlaneAuto(
    params.cargoType,
    params.cargoTypeStatus,
    params.flowBooking
  );

  const conditionRedirect = isRebooking || isRtcPlaneAuto || isUseSabre;

  const returnPath = getReturnPath(conditionRedirect);

  resetCargoData(conditionRedirect, params.router, returnPath);
  // CargoController.setProgressValue(-1);
};

export const getConfigDimensionDivider = (cargoType = ""): number => {
  const dividerConfig = FlagsPermissionCargo.config_dimension_divider.getValue();

  return dividerConfig?.[cargoType] ?? dividerConfig?.default ?? 6000;
};

export const getConfigIsminimumVolumePerPiece = (
  cargoType = "",
  cargoTypeStatus = ""
): boolean => {
  const flag = FlagsPermissionCargo.validation_3lc_cargo_minimum_weight.getValue();

  if (cargoType === "plane" && cargoTypeStatus === "auto") return !!flag;
  return false;
};

export const getConfigMinimumVolumePerPiece = (
  cargoType = "",
  cargoTypeStatus = ""
) => {
  const flag = FlagsPermissionCargo.validation_3lc_cargo_minimum_weight.getValue();

  if (cargoType === "plane" && cargoTypeStatus === "auto") return flag;
  return 0;
};

export const getConfigMaxPieces = (cargoType = "") => {
  const config = FlagsPermissionCargoV2.weight_validation_dsipute.getValue();
  const defaultPiece = cargoType == "plane" ? 15 : 1000;
  return config?.[cargoType]?.piece ?? defaultPiece;
};

export const getConfigMaxWeight = (cargoType = "") => {
  const config = FlagsPermissionCargoV2.weight_validation_dsipute.getValue();
  const defaultWeight = cargoType == "plane" ? 400 : 10000;
  return config?.[cargoType]?.weight ?? defaultWeight;
};

// Airport-to-airport route-type (GENESIS-24945): Horde `/horde/v1/airport/route-type`
// returns the three resolved booking limits for the current city pair. The BE
// already decides domestic/international; FE just caches the three numbers and
// never derives limits from `routeType` or country comparison.
export interface AirportRouteTypeCache {
  key: string;
  maxGrossWeightKg: number;
  maxVolumeWeightKg: number;
  maxPieces: number;
}

export const buildAirportRouteTypeCacheKey = (
  originCityCode: string,
  destinationCityCode: string
): string => `${originCityCode}|${destinationCityCode}`;

// Shared guard used by both the dispute-form watcher and the async fetch action
// (CargoV2Controller.refreshAirportRouteType) so the two can never disagree on
// when the Horde route-type call applies.
export const isPlaneRouteWithKnownCities = (
  cargoType: string,
  originCityCode: string,
  destinationCityCode: string
): boolean =>
  cargoType === "plane" && !!originCityCode && !!destinationCityCode;

// GENESIS-24946: single source of truth for plane booking limits used by both
// the validation getters (CargoV2Controller) and the "Maksimal ..." form text
// (cargo-dispute-form). Prefer the three BE-resolved numbers when the cache is
// valid for the current city pair; otherwise fall back to the shared helpers
// (plane default 400 kg gross / 400 kg volume / 15 koli — matches Horde default).
export interface PlaneBookingLimits {
  maxGross: number;
  maxVolume: number;
  maxPieces: number;
}

export const resolvePlaneBookingLimits = (
  cache: AirportRouteTypeCache | null,
  cargoType: string,
  originCityCode: string,
  destinationCityCode: string
): PlaneBookingLimits => {
  const fallback: PlaneBookingLimits = {
    maxGross: getConfigMaxWeight(cargoType),
    maxVolume: getConfigMaxWeight(cargoType),
    maxPieces: getConfigMaxPieces(cargoType)
  };

  if (
    !isPlaneRouteWithKnownCities(cargoType, originCityCode, destinationCityCode)
  ) {
    return fallback;
  }

  const key = buildAirportRouteTypeCacheKey(
    originCityCode,
    destinationCityCode
  );
  if (!cache || cache.key !== key) return fallback;

  return {
    maxGross: cache.maxGrossWeightKg,
    maxVolume: cache.maxVolumeWeightKg,
    maxPieces: cache.maxPieces
  };
};

export const CALCULATE_TYPE = {
  INIT: "INIT",
  CHANGE_GW_OR_PCS: "CHANGE_GW_OR_PCS",
  CHANGE_DIMENSION: "CHANGE_DIMENSION",
  CHANGE_MODA: "CHANGE_MODA",
  CHANGE_GW: "CHANGE_GW",
  CHANGE_PCS: "CHANGE_PCS"
};

export const SESSION_STORAGE_CARGO_KEY = {
  UPLOAD_PROOF_PHOTO_KEY: "is-upload-proof-photo",
  CALCULATE_TYPE: "calculate-type",
  CALCULATE_VOLUME: "calculate-volume",
  INIT_CALCULATE_TYPE: "init-calculate-type"
};

export function getDominantCommodityName(
  items: Array<{ commodityName: string }>
): string {
  if (!items.length) return "";
  const countByCommodity = items.reduce<Record<string, number>>((acc, item) => {
    const name = item.commodityName.trim();
    if (!name) return acc;
    acc[name] = (acc[name] ?? 0) + 1;
    return acc;
  }, {});
  const entries = Object.entries(countByCommodity);
  if (!entries.length) return "";
  const dominant = entries.reduce((a, b) => (a[1] >= b[1] ? a : b));
  return dominant[0];
}
